import React, { useState } from "react";
import { Typography, Chip, Avatar } from "@mui/material";
import CustomTable from "../../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
// import AddClient from "../AddClient/AddClient";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useLoading } from "../../../contexts/LoadingContext";
import Modal from "../../../components/Modal/Modal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import BusinessIcon from "@mui/icons-material/Business";
import { useAllProjectMilestoneViewModel } from "./AllProjectMilestoneViewModel";
import AddProjectMilestone from "../AddProjectMilestone/AddProjectMilestone";
import { useRbac } from "../../../hooks/RbacContext";
import { getEnumLabel } from "../../../utils/enumUtils";

const AllProjectMilestones = () => {
    const {
        projectMilestones,
        resources,
        projects,
        loading,
        error,
        getResourceName,
        getProjectName,
        createProjectMilestone,
        updateProjectMilestone,
        handleDelete,
        refetch,
        roles,
        getRoleName
    } = useAllProjectMilestoneViewModel();
    const { showLoading, hideLoading } = useLoading();

    const [isAddProjectMilestoneOpen, setIsAddProjectMilestoneOpen] = useState(false);
    const [selectedProjectMilestone, setSelectedProjectMilestone] = useState(null);
    const [successModal, setSuccessModal] = useState(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);
    const { checkAccess } = useRbac();
    const columns = [
        {
            id: "project_id",
            label: "Project",
            render: (value) => (
                <Typography
                    variant="body2"
                    noWrap
                    sx={{
                        fontSize: "inherit",
                        color: (theme) =>
                            value ? theme.palette.text.primary : theme.palette.text.secondary,
                    }}
                >
                    {getProjectName(value)}
                </Typography>
            ),
            searchValue: (value) => getProjectName(value),
        },
        {
            id: "milestone_name",
            label: "Milestone Name",
            align: "left"
        },
        {
            id: "milestone_description",
            label: "Milestone Description",
            align: "left"
        },
        {
            id: "milestone_start_date",
            label: "Start Date",
            align: "left",
        },
        {
            id: "milestone_end_date",
            label: "End Date",
            align: "left",
        },
        {
            id: "milestone_status_code",
            label: "Status",
            render: (value) => getEnumLabel("milestone_status_code", value),
            align: "left"
        },
        {
            id: "created_on",
            label: "Created On",
            align: "left",
        },

        {
            id: "updated_on",
            label: "Updated On",
            align: "left",
        }
    ];
    const fixedColumns = {
        left: [],
        right: ["is_active", "actions"],
    };
    const handleAddClient = () => {
        setSelectedProjectMilestone(null);
        setIsAddProjectMilestoneOpen(true);
    };

    const handleEditClient = (client) => {
        setSelectedProjectMilestone(client);
        setIsAddProjectMilestoneOpen(true);
    };

    const handleCloseDialog = () => {
        setIsAddProjectMilestoneOpen(false);
        setSelectedProjectMilestone(null);
    };

    const handleSubmit = async (formData) => {
        try {
            showLoading();
            if (selectedProjectMilestone) {
                await updateProjectMilestone(selectedProjectMilestone, formData);
                setSuccessModal({
                    type: "success",
                    title: "Success",
                    message: "Project Milestone updated successfully",
                });
            } else {
                await createProjectMilestone(formData);
                setSuccessModal({
                    type: "success",
                    title: "Success",
                    message: "Project Milestone added successfully",
                });
            }
            handleCloseDialog();
            await refetch();
        } catch (error) {
            throw error;
        } finally {
            hideLoading();
        }
    };

    const handleCloseSuccessModal = () => {
        setSuccessModal(null);
    };

    const handleDeleteClick = (projectResourceMapping) => {
        setDeleteConfirmation({
            projectResourceMapping,
            open: true,
        });
    };

    const handleDeleteConfirm = async () => {
        const projectResourceMapping = deleteConfirmation.projectResourceMapping;
        try {
            showLoading();
            await handleDelete(projectResourceMapping);
            setDeleteConfirmation(null);
            setSuccessModal({
                type: "success",
                title: "Success",
                message: "Project Milestone deleted successfully",
            });
        } catch (error) {
            setSuccessModal({
                type: "error",
                title: "Action Failed",
                message: error.message || "Failed to delete Project Milestone",
            });
        } finally {
            hideLoading();
        }
    };

    const getActions = (client) => {

        const actions = [];
        if (checkAccess("milestones", "update")) {
            actions.push({
                icon: <EditIcon fontSize="small" />,
                label: "Edit",
                onClick: handleEditClient,
                color: "primary",
                sx: {
                    "&:hover": {
                        backgroundColor: (theme) =>
                            theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
                    },
                },
            });
        }
        if (checkAccess("milestones", "delete")) {
            actions.push({
                icon: <DeleteIcon fontSize="small" />,
                label: "Delete",
                onClick: handleDeleteClick,
                color: "error",
                sx: {
                    "&:hover": {
                        backgroundColor: (theme) =>
                            theme.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
                    },
                },
            });
        }

        return actions;
    };

    if (error) {
        return (
            <div>
                <Typography color="error">Error: {error}</Typography>
            </div>
        );
    }
    const columnFilters = [
        {
            id: "project_id",
            type: "select",
            label: "Project",
            render: (value) => getProjectName(value),
            icon: <BusinessIcon />,
            sendIcon: true,
        }
    ];

    return (
        <div>
            <CustomTable
                columns={columns}
                data={projectMilestones}
                onAdd={handleAddClient}
                showActions={true}
                showAddButton={checkAccess("milestones", "add")}
                addButtonIcon={<PersonAddIcon />}
                showSearch={true}
                showColumnVisibility={false}
                pageSize={10}
                stickyHeader={true}
                fixedColumns={fixedColumns}
                actions={getActions}
                title="All Project Milestone"
                subtitle="Manage and monitor all Project Milestone"
                addButtonText="Add Project Milestone"
                alignment="left"
                fontSize="12px"
                showFilters={true}
                columnFilters={columnFilters}
            />

            <AddProjectMilestone
                open={isAddProjectMilestoneOpen}
                onClose={handleCloseDialog}
                initialData={selectedProjectMilestone}
                onSubmit={handleSubmit}
                resources={resources}
                getResourceName={getResourceName}
                projects={projects}
                getProjectName={getProjectName}
                roles={roles}

            />

            {successModal && (
                <Modal
                    type={successModal.type}
                    title={successModal.title}
                    message={successModal.message}
                    onClose={handleCloseSuccessModal}
                />
            )}

            {deleteConfirmation && (
                <ConfirmationModal
                    open={true}
                    title="Delete Project Milestone"
                    message={`Are you sure you want to delete Project Milestone "${deleteConfirmation.projectResourceMapping.milestone_name}"?`}
                    onClose={() => setDeleteConfirmation(null)}
                    onConfirm={handleDeleteConfirm}
                />
            )}
        </div>
    );
};

export default AllProjectMilestones;
